/**
 * ComplianceSummaryStrip
 *
 * Shared campaign-level compliance filter bar for advisor and reviewer
 * surfaces. Callers own the data derivation; this component owns the
 * visual language and button behavior.
 */

import React from 'react';
import { Box, Button, HStack, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { Check, X, AlertTriangle, MinusCircle, ListFilter, CircleHelp } from 'lucide-react';

import { COMPLIANCE_FILTERS } from '../../../utils/complianceFilters';

const FILTER_DEFS = [
  {
    key: COMPLIANCE_FILTERS.ALL,
    label: 'All',
    icon: ListFilter,
    color: 'rgba(255,255,255,0.72)',
    bgActive: 'rgba(255,255,255,0.08)',
    borderActive: 'rgba(255,255,255,0.28)',
    summaryKey: 'total',
    tooltip: 'Show every message draft row',
  },
  {
    key: COMPLIANCE_FILTERS.COMPLIANT,
    label: 'Compliant',
    icon: Check,
    color: '#34D399',
    bgActive: 'rgba(52,211,153,0.14)',
    borderActive: 'rgba(52,211,153,0.42)',
    summaryKey: 'compliant',
    tooltip: "Drafts a reviewer has approved and that haven't changed since",
  },
  {
    key: COMPLIANCE_FILTERS.NON_COMPLIANT,
    label: 'Flagged',
    icon: X,
    color: '#EF4444',
    bgActive: 'rgba(239,68,68,0.14)',
    borderActive: 'rgba(239,68,68,0.42)',
    summaryKey: 'nonCompliant',
    tooltip: 'Drafts a reviewer marked non-compliant - click a red chip to see the note',
  },
  {
    key: COMPLIANCE_FILTERS.NEEDS_HUMAN_REVIEW,
    label: 'Needs human',
    icon: CircleHelp,
    color: '#A78BFA',
    bgActive: 'rgba(167,139,250,0.14)',
    borderActive: 'rgba(167,139,250,0.42)',
    summaryKey: 'needsHumanReview',
    tooltip:
      'AI or reviewer flagged ambiguous or insufficient facts — human judgment required before sending',
  },
  {
    key: COMPLIANCE_FILTERS.STALE,
    label: 'Changed since review',
    icon: AlertTriangle,
    color: '#FBBF24',
    bgActive: 'rgba(251,191,36,0.14)',
    borderActive: 'rgba(251,191,36,0.42)',
    summaryKey: 'stale',
    tooltip: "Subject or body has changed since the reviewer's verdict - re-check before sending",
  },
  {
    key: COMPLIANCE_FILTERS.NOT_REVIEWED,
    label: 'Not reviewed',
    icon: MinusCircle,
    color: 'rgba(255,255,255,0.62)',
    bgActive: 'rgba(255,255,255,0.06)',
    borderActive: 'rgba(255,255,255,0.22)',
    summaryKey: 'notReviewed',
    tooltip: 'Drafts a reviewer has not yet decided on',
  },
];

const ComplianceSummaryStrip = ({ isEnabled, summary, filter, onFilterChange, isCountLoading = false }) => {
  if (!isEnabled) return null;
  if (!summary || summary.total === 0) return null;

  return (
    <Box
      px={4}
      py={2}
      bg="#0a0a0a"
      borderBottom="1px solid"
      borderColor="rgba(255,255,255,0.06)"
    >
      <HStack spacing={2} align="center">
        <Text
          fontSize="11px"
          color="rgba(255,255,255,0.48)"
          letterSpacing="0.04em"
          textTransform="uppercase"
          fontWeight="600"
          flexShrink={0}
          mr={1}
        >
          Compliance
        </Text>
        {FILTER_DEFS.map(({ key, label, icon: Icon, color, bgActive, borderActive, summaryKey, tooltip }) => {
          const count = summary[summaryKey] ?? 0;
          const isActive = filter === key;
          return (
            <Tooltip
              key={key}
              label={tooltip}
              placement="top"
              openDelay={300}
              bg="rgba(20,20,30,0.95)"
              color="whiteAlpha.800"
              fontSize="xs"
              borderRadius="6px"
              px={2}
              py={1}
              hasArrow
            >
              <Button
                size="xs"
                variant="unstyled"
                onClick={() => onFilterChange?.(key)}
                aria-pressed={isActive}
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                px={2.5}
                h="26px"
                borderRadius="full"
                borderWidth="1px"
                borderStyle="solid"
                borderColor={isActive ? borderActive : 'rgba(255,255,255,0.10)'}
                bg={isActive ? bgActive : 'transparent'}
                color={color}
                fontSize="12px"
                fontWeight="500"
                transition="background 0.15s ease, border-color 0.15s ease"
                _hover={{
                  bg: bgActive,
                  borderColor: borderActive,
                }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              >
                <Box as="span" display="inline-flex" alignItems="center" flexShrink={0}>
                  <Icon size={12} strokeWidth={2.25} />
                </Box>
                <Box as="span">{label}</Box>
                <Box
                  as="span"
                  px={1.5}
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="600"
                  bg="rgba(255,255,255,0.06)"
                  color="rgba(255,255,255,0.78)"
                  minW="18px"
                  textAlign="center"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {isCountLoading
                    ? <Spinner size="xs" speed="0.65s" color="rgba(255,255,255,0.45)" thickness="1.5px" w="9px" h="9px" />
                    : count}
                </Box>
              </Button>
            </Tooltip>
          );
        })}
      </HStack>
    </Box>
  );
};

export default ComplianceSummaryStrip;
